import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { X, Check, Search } from 'lucide-react';
import './MapPickerModal.css';
import { getMapboxToken } from '../../../utils/utils';
import { toast } from 'react-toastify';

mapboxgl.accessToken = getMapboxToken();

const DEFAULT_COORDS = [51.5310, 25.2854]; // Doha Qatar [lon, lat]

const MapPickerModal = ({ isOpen, onClose, initialCoords, onConfirm }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const mapReady = useRef(false);

  const [selectedCoords, setSelectedCoords] = useState(null); // [lon, lat]
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const lastSelectedName = useRef('');

  // Sync initial coords when opening
  useEffect(() => {
    if (isOpen) {
      setSearchResults([]);
      setSearchQuery('');
      lastSelectedName.current = '';
      if (initialCoords && initialCoords.length === 2 && !isNaN(initialCoords[0]) && !isNaN(initialCoords[1])) {
        setSelectedCoords([Number(initialCoords[1]), Number(initialCoords[0])]); // Mapbox uses [lon, lat]
      } else {
        setSelectedCoords(DEFAULT_COORDS);
      }
    }
  }, [isOpen, initialCoords]);

  // Debounce search query as user types
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 3) {
      setSearchResults([]);
      return;
    }

    if (trimmed === lastSelectedName.current) {
      return;
    }

    const timer = setTimeout(() => {
      performSearch(trimmed, true);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      if (!mapContainer.current || map.current) return;

      const center = selectedCoords || DEFAULT_COORDS;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: center,
        zoom: 12,
        interactive: true
      });

      map.current.addControl(
        new mapboxgl.NavigationControl({ showCompass: false }),
        'top-right'
      );

      map.current.on('load', () => {
        mapReady.current = true;

        // Add picker marker
        marker.current = new mapboxgl.Marker({ draggable: true })
          .setLngLat(center)
          .addTo(map.current);

        marker.current.on('dragend', () => {
          const lngLat = marker.current.getLngLat();
          setSelectedCoords([lngLat.lng, lngLat.lat]);
        });

        // Click on map to move marker
        map.current.on('click', (e) => {
          const { lng, lat } = e.lngLat;
          marker.current.setLngLat([lng, lat]);
          setSelectedCoords([lng, lat]);
        });
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      mapReady.current = false;
      if (marker.current) {
        marker.current.remove();
        marker.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [isOpen]);

  // Handle updates to selectedCoords (e.g. from search)
  useEffect(() => {
    if (!map.current || !marker.current || !selectedCoords) return;
    marker.current.setLngLat(selectedCoords);
    map.current.flyTo({ center: selectedCoords, zoom: 14 });
  }, [selectedCoords]);

  const performSearch = async (query, isAuto = false) => {
    if (!query) return;
    try {
      setSearching(true);
      const token = getMapboxToken();
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?country=QA&proximity=51.5310,25.2854&types=neighborhood,address,poi,place&language=en&limit=6&access_token=${token}`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const results = data.features.map(f => ({
          id: f.id,
          name: f.place_name,
          lng: f.center[0],
          lat: f.center[1]
        }));

        if (!isAuto && results.length === 1) {
          lastSelectedName.current = results[0].name;
          setSelectedCoords([results[0].lng, results[0].lat]);
          setSearchQuery(results[0].name);
          setSearchResults([]);
        } else {
          setSearchResults(results);
        }
      } else {
        if (!isAuto) {
          toast.error('Location not found in Qatar. Please try a different query.');
        }
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Geocoding search failed:', err);
      if (!isAuto) {
        toast.error('Search request failed.');
      }
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    performSearch(searchQuery.trim(), false);
  };

  const selectSuggestion = (result) => {
    lastSelectedName.current = result.name;
    setSelectedCoords([result.lng, result.lat]);
    setSearchQuery(result.name);
    setSearchResults([]);
  };

  const handleConfirm = () => {
    if (!selectedCoords) return;
    // Return coordinates as [latitude, longitude]
    onConfirm(selectedCoords[1], selectedCoords[0]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="map-picker-modal-overlay" onClick={onClose}>
      <div className="map-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="map-picker-modal-header">
          <h3>Pick Location on Map</h3>
          <button className="map-picker-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSearch} className="map-picker-search-container">
          <input
            type="text"
            className="map-picker-search-input"
            placeholder="Search address or area (e.g. Pearl Qatar)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="map-picker-search-btn" disabled={searching}>
            {searching ? 'Searching...' : <Search size={16} />}
          </button>

          {searchResults.length > 0 && (
            <ul className="map-picker-results">
              {searchResults.map((result) => (
                <li key={result.id}>
                  <button type="button" onClick={() => selectSuggestion(result)}>
                    {result.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </form>

        <div className="map-picker-modal-map-container" ref={mapContainer}>
          <div className="map-picker-pin-instructions">
            Click map to pick, or drag the marker
          </div>
        </div>

        <div className="map-picker-selected-info">
          <div className="map-picker-coord-row">
            <span className="map-picker-coord-label">Picked Coordinates:</span>
            <span className="map-picker-coord-value">
              {selectedCoords ? `${selectedCoords[1].toFixed(6)}, ${selectedCoords[0].toFixed(6)}` : 'None'}
            </span>
          </div>
        </div>

        <div className="map-picker-modal-footer">
          <button className="map-picker-modal-cancel" onClick={onClose}>Cancel</button>
          <button
            className="map-picker-modal-confirm"
            onClick={handleConfirm}
            disabled={!selectedCoords}
          >
            <Check size={16} />
            Confirm Location
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapPickerModal;
